// File: src/pages/Contact.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../styles/PhoneInput.css';

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });

  // Form validation state
  const [formValid, setFormValid] = useState(false);

  // reCAPTCHA state
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  // Check if form is valid whenever form data or captcha state changes
  useEffect(() => {
    const { fullName, email, phone, message } = formData;
    const isFormFilled = fullName.trim() !== '' &&
      email.trim() !== '' &&
      phone.trim() !== '' &&
      message.trim() !== '';

    setFormValid(isFormFilled && captchaVerified);
  }, [formData, captchaVerified]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle phone input changes
  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value,
    });
  };

  // Handle reCAPTCHA verification
  const handleCaptchaChange = (value) => {
    setCaptchaVerified(!!value);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formValid) return;

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');

      // Reset form after successful submission
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        message: '',
      });
      setCaptchaVerified(false);

      // Reset reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }, 1500);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#fbfbfb'
    }}>
      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{
            color: '#0a233b',
            fontWeight: 'bold',
          }}>
            Contact Us
          </Typography>
          <Button
            variant="contained"
            startIcon={<HelpOutlineIcon />}
            onClick={() => navigate('/faq')}
            sx={{
              bgcolor: '#f66b10',
              '&:hover': { bgcolor: '#e05a00' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            View FAQ
          </Button>
        </Box>

        <Grid container spacing={3} sx={{ height: 'calc(85% - 70px)' }}>
          {/* Contact Information Section */}
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{
              p: 3,
              height: '100%',
              backgroundColor: '#0a233b',
              color: '#fbfbfb',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#a6d1ea' }}>
                Get In Touch
              </Typography>

              <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                We'd love to hear from you. Please feel free to contact us using any of the following methods:
              </Typography>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PhoneIcon sx={{ color: '#a6d1ea', mr: 2, fontSize: '1.8rem' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#a6d1ea' }}>
                      Phone
                    </Typography>
                    <Link
                      href="tel:+254736261808"
                      sx={{
                        color: '#fbfbfb',
                        textDecoration: 'none',
                        '&:hover': { color: '#f66b10' }
                      }}
                    >
                      0736261808
                    </Link>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WhatsAppIcon sx={{ color: '#a6d1ea', mr: 2, fontSize: '1.8rem' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#a6d1ea' }}>
                      WhatsApp
                    </Typography>
                    <Link
                      href="https://wa.me/254722575313"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#fbfbfb',
                        textDecoration: 'none',
                        '&:hover': { color: '#f66b10' }
                      }}
                    >
                      0722575313
                    </Link>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationOnIcon sx={{ color: '#a6d1ea', mr: 2, fontSize: '1.8rem' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#a6d1ea' }}>
                      Location
                    </Typography>
                    <Link
                      href="https://maps.google.com/?q=Ouru+Complex,+Kisii"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#fbfbfb',
                        textDecoration: 'none',
                        '&:hover': { color: '#f66b10' }
                      }}
                    >
                      Ouru Complex, 3rd floor (Above Equity), Kisii
                    </Link>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon sx={{ color: '#a6d1ea', mr: 2, fontSize: '1.8rem' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#a6d1ea' }}>
                      Email
                    </Typography>
                    <Link
                      href="mailto:lantenapmanagers@gmail.com"
                      sx={{
                        color: '#fbfbfb',
                        textDecoration: 'none',
                        '&:hover': { color: '#f66b10' }
                      }}
                    >
                      lantenapmanagers@gmail.com
                    </Link>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LanguageIcon sx={{ color: '#a6d1ea', mr: 2, fontSize: '1.8rem' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ color: '#a6d1ea' }}>
                      Website
                    </Typography>
                    <Link
                      href="https://www.lantenapm.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#fbfbfb',
                        textDecoration: 'none',
                        '&:hover': { color: '#f66b10' }
                      }}
                    >
                      www.lantenapm.com
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Contact Form Section */}
          <Grid item xs={12} md={7}>
            <Paper elevation={3} sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#0a233b' }}>
                Send Us a Message
              </Typography>

              <Typography variant="body1" paragraph sx={{ color: '#6a6565', mb: 2 }}>
                Fill out the form below and we'll get back to you as soon as possible.
              </Typography>

              {submitStatus === 'success' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Your message has been sent successfully! We'll get back to you soon.
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 1.5 }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  sx={{ mb: 1.5 }}
                />

                <Box sx={{ mt: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Your Mobile Number (*)
                  </Typography>
                  <PhoneInput
                    country={'ke'}
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    enableSearch
                    searchPlaceholder="Search country"
                    inputProps={{
                      name: 'phone',
                      required: true,
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={3}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#a6d1ea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#a6d1ea',
                      },
                    },
                  }}
                />

                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" // This is Google's test key - replace with your actual key in production
                    onChange={handleCaptchaChange}
                    theme="light"
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 'auto' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!formValid || isSubmitting}
                    endIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      backgroundColor: '#f66b10',
                      '&:hover': {
                        backgroundColor: '#d45500',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#6a6565',
                        color: '#fbfbfb',
                      }
                    }}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ height: '30px' }} />

      <Box sx={{ width: '100%', height: '250px' }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7891642636327!2d34.76972!3d-0.67778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182b39f5e8c39bbd%3A0x3c56d48b2b7e8d9a!2sOuru%20Complex%2C%20Kisii!5e0!3m2!1sen!2ske!4v1616661234567!5m2!1sen!2ske"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Lantena Office Location"
        />
      </Box>
    </Box>
  );
};

export default Contact;
